import { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { apiSignup, apiResendVerification, apiErrorCode, apiErrorMessage, apiGetPublicConfig } from '../api';
import PasswordInput from '../components/PasswordInput';
import BandzLogo from '../components/BandzLogo';

/**
 * Public self-service sign-up page (/signup).
 *
 * Public route, OUTSIDE RequireAuth — same model as /accept-invitation: the
 * visitor has no account yet, so a redirect to /login would trap them.
 *
 * The server never creates a session here (`verification_required`), so a
 * successful submit switches to the "Check your email" screen instead of
 * navigating: the visitor becomes signed in only by clicking the link in the
 * e-mail, which lands on /verify-email.
 *
 * Errors are mapped BY CODE (the server's frozen codes) and fall back to the
 * server's English `message` when the code is unknown.
 */
const ERROR_KEYS: Record<string, string> = {
  invalid_email: 'signup.errInvalidEmail',
  invalid_username: 'signup.errInvalidUsername',
  weak_password: 'signup.errWeakPassword',
  email_taken: 'signup.errEmailTaken',
  username_taken: 'signup.errUsernameTaken',
  too_many_requests: 'signup.errTooManyRequests',
  spam_detected: 'signup.errSpam',
  disposable_email: 'signup.errDisposableEmail',
  invalid_email_domain: 'signup.errInvalidEmailDomain',
  // The instance is closed (server/lib/features.js) — e.g. prod soft-launch.
  signup_disabled: 'signup.closedTitle',
};

export default function SignupPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  // Anti-abuse: the hidden honeypot value (always empty for a human) and the
  // time spent on the form. Both are checked server-side — never trusted here.
  const [website, setWebsite] = useState('');
  const mountedAt = useRef(Date.now());
  // Server-authoritative: is self-service registration open on THIS instance?
  // (see server/lib/features.js). Defaults to OPEN; the server still refuses
  // with 403 `signup_disabled` — this only avoids showing a dead form.
  const [closed, setClosed] = useState(false);

  useEffect(() => {
    let alive = true;
    apiGetPublicConfig().then((c) => { if (alive) setClosed(!c.signupEnabled); });
    return () => { alive = false; };
  }, []);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError(t('signup.errPasswordMismatch'));
      return;
    }
    setBusy(true);
    try {
      const name = displayName.trim();
      await apiSignup({
        email: email.trim(),
        username: username.trim(),
        password,
        ...(name ? { display_name: name } : {}),
        website,
        elapsed_ms: Date.now() - mountedAt.current,
      });
      // No session: the account must be verified by e-mail first.
      setDone(true);
    } catch (err) {
      const code = apiErrorCode(err);
      const key = code ? ERROR_KEYS[code] : undefined;
      setError(key ? t(key) : (apiErrorMessage(err) || t('signup.errFailed')));
    } finally {
      setBusy(false);
    }
  }

  async function resend() {
    setResending(true);
    setError(null);
    try {
      await apiResendVerification(email.trim());
      setResent(true);
    } catch (err) {
      const code = apiErrorCode(err);
      setError(code === 'too_many_requests' ? t('signup.errTooManyRequests') : t('signup.resendFailed'));
    } finally {
      setResending(false);
    }
  }

  if (closed) {
    return (
      <div className="login-page">
        <div className="login-wrap">
          <BandzLogo className="login-logo" />
          <div className="login-card">
            <h1>{t('signup.closedTitle')}</h1>
            <p className="login-subtitle">{t('signup.closedBody')}</p>
            <p className="signup-alt">
              <Link to="/login">{t('signup.backToLogin')}</Link>
            </p>
          </div>
          <p className="login-demo-note">{t('login.demoReadOnly')}</p>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="login-page">
        <div className="login-wrap">
          <BandzLogo className="login-logo" />
          <div className="login-card">
            <h1>{t('signup.checkTitle')}</h1>
            <p className="login-subtitle">{t('signup.checkBody', { email })}</p>
            <p className="signup-note">{t('signup.checkHint')}</p>
            {resent && <div className="signup-ok">{t('signup.resent')}</div>}
            {error && <div className="login-error">{error}</div>}
            <button type="button" className="login-btn" onClick={resend} disabled={resending}>
              {resending ? t('signup.resending') : t('signup.resend')}
            </button>
            <p className="signup-alt">
              <Link to="/login">{t('signup.signIn')}</Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-wrap">
        <BandzLogo className="login-logo" />
        <div className="login-card">
          <h1>{t('signup.title')}</h1>
          <p className="login-subtitle">{t('signup.subtitle')}</p>
          <form onSubmit={onSubmit}>
            <label>
              {t('signup.email')}
              <input
                type="email"
                autoFocus
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
              />
            </label>
            <label>
              {t('signup.username')}
              <input
                type="text"
                required
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
              />
            </label>
            <label>
              {t('signup.password')}
              <PasswordInput
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
              />
            </label>
            <p className="signup-note">{t('signup.pwHint')}</p>
            <label>
              {t('signup.confirmPassword')}
              <PasswordInput
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
              />
            </label>
            <label>
              {t('signup.displayName')}
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                autoComplete="name"
              />
            </label>
            <p className="signup-note">{t('signup.displayNameHint')}</p>
            {/* Anti-abuse honeypot: off-screen, aria-hidden and out of the tab
                order, so a human never sees or fills it. A bot that autofills
                every input trips it and the server answers `spam_detected`.
                Positioned (not `display:none`) on purpose: hidden-by-display
                inputs are commonly skipped by form-filling bots.
                Deliberately NOT wrapped in a <label>: the form exposes exactly
                the 5 fields a human fills (asserted by qa-signup-pages.cjs). */}
            <div
              aria-hidden="true"
              style={{ position: 'absolute', left: '-9999px', top: 0, width: 1, height: 1, overflow: 'hidden' }}
            >
              <input
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                aria-label="Website"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
              />
            </div>
            {error && <div className="login-error">{error}</div>}
            <button type="submit" className="login-btn" disabled={busy}>
              {busy ? t('signup.submitting') : t('signup.submit')}
            </button>
          </form>
          <p className="signup-alt">
            {t('signup.haveAccount')} <Link to="/login">{t('signup.signIn')}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}